import React from 'react';
import { observer } from 'mobx-react-lite';
import { 
  User, 
  Users, 
  Database, 
  HardDrive, 
  Server, 
  Cloud, 
  Monitor, 
  Smartphone, 
  Globe, 
  Shield, 
  Key, 
  Cpu, 
  MemoryStick, 
  Wifi, 
  Router, 
  Layers, 
  Package, 
  Settings, 
  Activity, 
  BarChart3,
} from 'lucide-react';
import type { IconElement } from '@/types';

interface IconComponentProps {
  element: IconElement;
  isSelected: boolean;
}

// Map of icon names to their original Lucide components
const ICON_COMPONENTS = {
  User,
  Users,
  Database,
  HardDrive,
  Server,
  Cloud,
  Monitor,
  Smartphone,
  Globe,
  Shield,
  Key,
  Cpu,
  MemoryStick,
  Wifi,
  Router,
  Layers,
  Package,
  Settings,
  Activity,
  BarChart3,
} as const;

export const IconComponent: React.FC<IconComponentProps> = observer(({ element, isSelected }) => {
  const { x, y, width, height, iconName, style, text } = element;
  
  const IconLucideComponent = ICON_COMPONENTS[iconName as keyof typeof ICON_COMPONENTS];
  
  if (!IconLucideComponent) {
    // Fallback for unknown icons
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="none"
          stroke={style.strokeColor}
          strokeWidth={style.strokeWidth}
          opacity={style.opacity}
        />
        <text
          x={x + width/2}
          y={y + height/2}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="12"
          fill={style.strokeColor === 'transparent' ? '#000000' : style.strokeColor}
        >
          ?
        </text>
      </g>
    );
  }

  // Calculate icon size and position - keep it centered
  const iconSize = Math.min(width, height) * 0.8;
  const iconX = x + (width - iconSize) / 2;
  const iconY = y + (height - iconSize) / 2;

  return (
    <g>
      {/* Icon wrapper with stroke color override */}
      <g 
        transform={`translate(${iconX}, ${iconY})`}
        style={{
          color: style.strokeColor === 'transparent' ? '#000000' : style.strokeColor,
          opacity: style.opacity,
        }}
      >
        {/* Render the original Lucide icon with stroke color wrapper */}
        <foreignObject width={iconSize} height={iconSize}>
          <div 
            style={{
              width: iconSize,
              height: iconSize,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: style.strokeColor === 'transparent' ? '#000000' : style.strokeColor,
            }}
          >
            <IconLucideComponent 
              size={iconSize} 
              strokeWidth={style.strokeWidth}
              style={{
                color: 'inherit',
              }}
            />
          </div>
        </foreignObject>
      </g>
      
      {/* Optional text label */}
      {text && (
        <text
          x={x + width/2}
          y={y + height + 16}
          textAnchor="middle"
          fontSize={style.fontSize}
          fontFamily={style.fontFamily}
          fill={style.strokeColor === 'transparent' ? '#000000' : style.strokeColor}
          opacity={style.opacity}
        >
          {text}
        </text>
      )}

      {/* Selection indicators */}
      {isSelected && (
        <g>
          {/* Selection border */}
          <rect
            x={x - 2}
            y={y - 2}
            width={width + 4}
            height={height + 4}
            fill="none"
            stroke="#007acc"
            strokeWidth={2}
            strokeDasharray="5,5"
            rx={6}
          />
          {/* Resize handles */}
          <rect x={x - 6} y={y - 6} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x + width - 2} y={y - 6} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x - 6} y={y + height - 2} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x + width - 2} y={y + height - 2} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x + width/2 - 4} y={y - 6} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x + width/2 - 4} y={y + height - 2} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x - 6} y={y + height/2 - 4} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x + width - 2} y={y + height/2 - 4} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
        </g>
      )}
    </g>
  );
}); 